import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { AppProvider } from './contexts/AppProvider';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import { CookieConsent } from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import PageLoader from './components/PageLoader';
import PageErrorBoundary from './components/PageErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';
import './App.css';

// Lazy load all pages for better code splitting
const Home = lazyWithRetry(() => import('./pages/Home'), 'home');
const Features = lazyWithRetry(() => import('./pages/Features'), 'features');
const HowItWorks = lazyWithRetry(() => import('./pages/HowItWorks'), 'how-it-works');
const Pricing = lazyWithRetry(() => import('./pages/Pricing'), 'pricing');
const About = lazyWithRetry(() => import('./pages/About'), 'about');
const Privacy = lazyWithRetry(() => import('./pages/Privacy'), 'privacy');
const Terms = lazyWithRetry(() => import('./pages/Terms'), 'terms');
const Support = lazyWithRetry(() => import('./pages/Support'), 'support');
const CookiePolicy = lazyWithRetry(() => import('./pages/CookiePolicy'), 'cookie-policy');
const CCPAPrivacy = lazyWithRetry(() => import('./pages/CCPAPrivacy'), 'ccpa-privacy');
const AdminLogin = lazyWithRetry(() => import('./pages/AdminLogin'), 'admin-login');
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'), 'admin-dashboard');
const AdminAssignRole = lazyWithRetry(() => import('./pages/AdminAssignRole'), 'admin-assign-role');
const SystemStatus = lazyWithRetry(() => import('./pages/SystemStatus'), 'status');
const NotFound = lazyWithRetry(() => import('./pages/NotFound'), 'not-found');

// Component to handle navigation display
const AppContent = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const showNavigation = !location.pathname.startsWith('/admin'); // Hide navigation on admin pages
  
  return (
    <>
      {showNavigation && <Navigation />}
      <PageErrorBoundary
        key={location.pathname}
        title={t('page_error.title')}
        description={t('page_error.description')}
        reference={t('page_error.reference')}
        retryLabel={t('page_error.retry')}
      >
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Main Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<Support />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/ccpa-privacy" element={<CCPAPrivacy />} />
          <Route path="/status" element={<SystemStatus />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/assign-role" element={<AdminAssignRole />} />
          
          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </PageErrorBoundary>
      {showNavigation && <Footer />}
      <CookieConsent />
      <ScrollToTop />
    </>
  );
};

function App() {
  const { t } = useTranslation();

  return (
    <AppProvider>
      <Router>
        <div
          className="App" 
          role="application" 
          aria-label={t('common.app_aria')}
        >
          <AppContent />
        </div>
      </Router>
    </AppProvider>
  );
}

export default App;
